/**
 * [TD-121] useSnakeAutoDraftQueue - persistent auto-draft toggle for the
 * snake draft action panel.
 *
 * Locked behaviors:
 *   • Toggle is OFF by default. Persisted to localStorage scoped per-league
 *     per-user (`pgf_auto_draft_queue:{leagueId}:{userId}`).
 *   • When ON + it's the user's turn + the queue has at least one player
 *     not yet drafted by any team → auto-submit the top-most available
 *     queued player.
 *   • [Item 2] Drafted entries are pruned by `useDraftQueue`, so the queue
 *     head is normally already available; the `takenIds` skip stays as the
 *     guard for a pick that lands between renders.
 *   • [Item 2] ON with an EMPTY queue is a legal, deliberate state: no
 *     instant best-available grab here. The server pick timer's existing
 *     auto-pick covers it, exactly as it covers an absent manager, and the
 *     Queue panel warns the manager that's what will happen.
 *   • Single-flight guard prevents double-submit if the hook re-runs
 *     before the WS state transition lands.
 *
 * [Item 2] Mount this ONCE, via AutoDraftProvider around the snake shell -
 * never inside a right-rail tab. TabbedPanel unmounts inactive tabs, so a
 * tab-local instance would stop auto-drafting the moment the manager looked
 * at Pick History, and two instances would race their submits.
 *
 * Return: { enabled, setEnabled, hasQueue }
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDraftQueueContext } from "../contexts/DraftQueueContext";
import { useSnakeDraft } from "../contexts/SnakeDraftContext";

const STORAGE_PREFIX = "pgf_auto_draft_queue";

const storageKey = (leagueId, userId) =>
  `${STORAGE_PREFIX}:${leagueId || "_"}:${userId || "_"}`;

function hydrate(leagueId, userId) {
  if (!leagueId || !userId) return false;
  try {
    return localStorage.getItem(storageKey(leagueId, userId)) === "1";
  } catch {
    return false;
  }
}

function persist(leagueId, userId, enabled) {
  if (!leagueId || !userId) return;
  try {
    if (enabled) localStorage.setItem(storageKey(leagueId, userId), "1");
    else localStorage.removeItem(storageKey(leagueId, userId));
  } catch {
    /* quota / privacy mode - accept loss */
  }
}

export function useSnakeAutoDraftQueue(leagueId, userId) {
  const { queue, takenIds } = useDraftQueueContext();
  const { draftState, submitPick } = useSnakeDraft();
  const [enabled, setEnabledState] = useState(() => hydrate(leagueId, userId));
  const inFlightRef = useRef(false);

  // Persist on toggle change.
  const setEnabled = useCallback(
    (next) => {
      const v = typeof next === "function" ? next(enabled) : !!next;
      setEnabledState(v);
      persist(leagueId, userId, v);
    },
    [enabled, leagueId, userId],
  );

  // Re-hydrate when scope changes (different league / user).
  useEffect(() => {
    setEnabledState(hydrate(leagueId, userId));
  }, [leagueId, userId]);

  const hasQueue = queue.length > 0;
  const isMyTurn =
    !!userId &&
    draftState?.state === "DRAFTING" &&
    draftState?.current_picker_id === userId;

  // Top-of-queue auto-pick. Skip any queue entries that have already been
  // drafted by ANY team (cross-team picks stay in queue under TD-121).
  useEffect(() => {
    if (!enabled) return;
    if (!hasQueue) return;
    if (!isMyTurn) return;
    if (inFlightRef.current) return;
    const next = queue.find((p) => !takenIds.has(p.id));
    if (!next) return;
    inFlightRef.current = true;
    submitPick(next.id).finally(() => {
      // Release after one tick so a stale WS state doesn't double-trigger.
      setTimeout(() => {
        inFlightRef.current = false;
      }, 250);
    });
  }, [enabled, hasQueue, isMyTurn, queue, takenIds, submitPick]);

  return useMemo(
    () => ({ enabled, setEnabled, hasQueue }),
    [enabled, setEnabled, hasQueue],
  );
}
